import { Injectable } from '@angular/core';
import {
  FavoriteBlendState,
  FavoritePayloadV2,
  LegacySectionIndex,
} from './favorite.models';

export type FavoriteFormat = 'v2' | 'legacy' | 'invalid';

@Injectable({ providedIn: 'root' })
export class FavoriteParserService {

  /**
   * Detects whether raw blob content is JSON v2, legacy "$" format, or invalid.
   */
  detectFormat(raw: string): FavoriteFormat {
    if (!raw || raw.trim().length === 0) {
      return 'invalid';
    }
    const trimmed = raw.trim();
    if (trimmed.startsWith('{')) {
      try {
        if (this.isValidV2Payload(JSON.parse(trimmed))) return 'v2';
      } catch {
        return 'invalid';
      }
    }
    if (this.hasValidLegacyEnvelope(raw)) {
      return 'legacy';
    }
    return 'invalid';
  }

  /**
   * Auto-detect and parse any format.
   */
  parseAny(raw: string): FavoriteBlendState | null {
    const format = this.detectFormat(raw);
    switch (format) {
      case 'v2':
        return this.parseV2(raw);
      case 'legacy':
        return this.parseLegacy(raw);
      default:
        return null;
    }
  }

  /**
   * Parse JSON v2 format.
   */
  parseV2(raw: string): FavoriteBlendState | null {
    try {
      const payload: FavoritePayloadV2 = JSON.parse(raw.trim());
      return this.isValidV2Payload(payload) ? payload.blendState : null;
    } catch {
      return null;
    }
  }

  /**
   * Parse legacy "$" separated format from blob content.
   * Sections are separated by "0: $" (after joining object entries).
   */
  parseLegacy(raw: string): FavoriteBlendState | null {
    try {
      if (!this.hasValidLegacyEnvelope(raw)) return null;
      const sections = raw.split('0: $');

      return {
        finishedOilData: this.parseTableSection(sections[LegacySectionIndex.FinishedOil]),
        package1Pred1: this.parsePackageSection(sections[LegacySectionIndex.Package1Pred1]),
        package1Pred2: this.parsePackageSection(sections[LegacySectionIndex.Package1Pred2]),
        package2Pred1: this.parsePackageSection(sections[LegacySectionIndex.Package2Pred1]),
        package2Pred2: this.parsePackageSection(sections[LegacySectionIndex.Package2Pred2]),
        wtPer11: this.extractSingleValue(sections[LegacySectionIndex.WtPer11], /wtper11:\s+([\d.]+)/),
        wtPer12: this.extractSingleValue(sections[LegacySectionIndex.WtPer12], /wtper12:\s+([\d.]+)/),
        wtPer21: this.extractSingleValue(sections[LegacySectionIndex.WtPer21], /wtper21:\s+([\d.]+)/),
        wtPer22: this.extractSingleValue(sections[LegacySectionIndex.WtPer22], /wtper22:\s+([\d.]+)/),
        dilPer11: this.extractDilValue(sections[LegacySectionIndex.DilPer11]),
        dilPer12: this.extractDilValue(sections[LegacySectionIndex.DilPer12]),
        dilPer21: this.extractDilValue(sections[LegacySectionIndex.DilPer21]),
        dilPer22: this.extractDilValue(sections[LegacySectionIndex.DilPer22]),
        modInpDi: this.parseModelInputSection(sections[LegacySectionIndex.ModInpDi], 'di'),
        modInpVm: this.parseModelInputSection(sections[LegacySectionIndex.ModInpVm], 'vm'),
        modInpPpd: this.parseModelInputSection(sections[LegacySectionIndex.ModInpPpd], 'ppd'),
        modInpBo: this.parseModelInputSection(sections[LegacySectionIndex.ModInpBo], 'bo'),
        modInpColVm: this.parseColumnSection(sections[LegacySectionIndex.ModInpColVm]),
        modInpColBo: this.parseColumnSection(sections[LegacySectionIndex.ModInpColBo]),
        modInpColDi: this.parseColumnSection(sections[LegacySectionIndex.ModInpColDi]),
        modInpDi2: this.parseModelInputSection(sections[LegacySectionIndex.ModInpDi2], 'di'),
        modInpVm2: this.parseModelInputSection(sections[LegacySectionIndex.ModInpVm2], 'vm'),
        modInpPpd2: this.parseModelInputSection(sections[LegacySectionIndex.ModInpPpd2], 'ppd'),
        modInpBo2: this.parseModelInputSection(sections[LegacySectionIndex.ModInpBo2], 'bo'),
        modInpColVm2: this.parseColumnSection(sections[LegacySectionIndex.ModInpColVm2]),
        modInpColBo2: this.parseColumnSection(sections[LegacySectionIndex.ModInpColBo2]),
        modInpColDi2: this.parseColumnSection(sections[LegacySectionIndex.ModInpColDi2]),
        shareableLink: this.extractShareableLink(sections[LegacySectionIndex.ShareableLinks]),
      };
    } catch {
      return null;
    }
  }

  // --- Private helpers ---

  private hasValidLegacyEnvelope(raw: string): boolean {
    const sections = raw.split('0: $');
    return sections.length === 29
      && /^snum:\s*(?:[1-9]|[1-9]\d|100)\s*$/m.test(sections[LegacySectionIndex.SlotNumber].trim())
      && /(?:^|\n)0:\s*@\s*$/.test(sections[LegacySectionIndex.ShareableLinks]);
  }

  private isValidV2Payload(value: unknown): value is FavoritePayloadV2 {
    if (!value || typeof value !== 'object') return false;
    const payload = value as Partial<FavoritePayloadV2>;
    const slotNumber = Number(payload.slotNumber);
    if (payload.version !== 2 || !Number.isInteger(slotNumber) || slotNumber < 1 || slotNumber > 100) {
      return false;
    }
    if (!payload.blendState || typeof payload.blendState !== 'object') return false;

    const arrayFields: Array<keyof FavoriteBlendState> = [
      'finishedOilData',
      'package1Pred1', 'package1Pred2', 'package2Pred1', 'package2Pred2',
      'modInpDi', 'modInpVm', 'modInpPpd', 'modInpBo',
      'modInpColVm', 'modInpColBo', 'modInpColDi',
      'modInpDi2', 'modInpVm2', 'modInpPpd2', 'modInpBo2',
      'modInpColVm2', 'modInpColBo2', 'modInpColDi2',
    ];
    return arrayFields.every(field => Array.isArray(payload.blendState?.[field]));
  }

  /**
   * Extract usable lines from a section: split by newline, strip quotes, filter empties.
   * Matches legacy React: `.split("\n").slice(1, -1)`
   */
  private getSectionLines(section: string): string[] {
    if (!section) return [];
    return section
      .split('\n')
      .slice(1, -1) // skip first/last empty lines from split
      .map(line => this.stripQuotes(line.trim()))
      .filter(line => line.length > 0);
  }

  /** Strip surrounding double quotes from CSV-escaped lines */
  private stripQuotes(line: string): string {
    if (line.startsWith('"') && line.endsWith('"')) {
      return line.substring(1, line.length - 1);
    }
    return line;
  }

  /**
   * Parse finished oil section — rows like:
   * "Fixed_value: Package 1,Name: OLOA 99600,null,null,Treat_wt_P1: 25.4,null"
   */
  private parseTableSection(section: string): Record<string, string>[] {
    const lines = this.getSectionLines(section);
    return lines.map(line => this.parseKVLine(line));
  }

  /**
   * Parse package section (additives) with Typ. Metal % → Typ_Metal renaming.
   * Lines like: "Additive: OLOA 13300,Dosage: 2,Unit: wt%,..."
   */
  private parsePackageSection(section: string): Record<string, string>[] {
    const lines = this.getSectionLines(section);
    return lines.map(line => {
      const obj: Record<string, string> = {};
      const parts = line.split(',');
      parts.forEach(part => {
        const [key, value] = this.splitFirstColon(part);
        if (key === undefined || value === undefined) return;
        const trimmedKey = key.trim();
        let transformedKey = trimmedKey;
        if (trimmedKey === 'Typ. Metal %') {
          transformedKey = 'Typ_Metal';
        }
        const numericFields = ['Dosage', 'Wt% in Finished Oil', 'Wt% in DI Package', 'Typ. Metal %', 'Typ_Metal'];
        if (value.trim() !== 'null' && numericFields.includes(trimmedKey)) {
          obj[transformedKey] = parseFloat(value.trim()).toFixed(3);
        } else {
          obj[transformedKey] = value.trim();
        }
      });
      return obj;
    });
  }

  /**
   * Parse model input section (di, vm, ppd, bo) with appropriate numeric formatting.
   */
  private parseModelInputSection(section: string, type: 'di' | 'vm' | 'ppd' | 'bo'): Record<string, string>[] {
    const lines = this.getSectionLines(section);
    return lines.map(line => {
      const obj: Record<string, string> = {};
      const parts = line.split(',');
      parts.forEach(part => {
        const [key, value] = this.splitFirstColon(part);
        if (key === undefined || value === undefined) return;
        const trimmedKey = key.trim();
        const trimmedValue = value.trim();

        if (trimmedValue === 'null') {
          obj[trimmedKey] = trimmedValue;
          return;
        }

        let isText = false;
        if (type === 'vm' && trimmedKey === 'VM') isText = true;
        if (type === 'bo' && (trimmedKey === 'Base Oil' || trimmedKey === 'Batch')) isText = true;
        if (type === 'di' && !['Dosage', 'Typ. Metal %'].includes(trimmedKey)) isText = true;

        if (isText) {
          obj[trimmedKey] = trimmedValue;
        } else {
          obj[trimmedKey] = parseFloat(trimmedValue).toFixed(2);
        }
      });
      return obj;
    });
  }

  /**
   * Parse column section (mod_inp_col_*) — all values kept as strings.
   */
  private parseColumnSection(section: string): Record<string, string>[] {
    const lines = this.getSectionLines(section);
    return lines.map(line => {
      const obj: Record<string, string> = {};
      const parts = line.split(',');
      parts.forEach(part => {
        const [key, value] = this.splitFirstColon(part);
        if (key === undefined || value === undefined) return;
        if (value.trim() !== 'null') {
          obj[key.trim()] = value.trim();
        }
      });
      return obj;
    });
  }

  /** Extract a single numeric value using a regex pattern */
  private extractSingleValue(section: string, pattern: RegExp): string | null {
    if (!section) return null;
    const match = section.match(pattern);
    return match ? match[1] : null;
  }

  /** Extract dil percent value — format "dil11: 20.06" or "dil21: " (empty) */
  private extractDilValue(section: string): string | null {
    if (!section) return null;
    const match = section.match(/dil\d+:\s*(.*)/);
    if (match) {
      const val = match[1].trim();
      return val.length > 0 ? val : null;
    }
    return null;
  }

  /** Extract shareable link from section */
  private extractShareableLink(section: string | undefined): string | null {
    if (!section) return null;
    const match = section.match(/link:\s*(.+)/);
    return match ? match[1].trim() : null;
  }

  /** Parse a key-value line: "key: val,key: val,null,..." — nulls are skipped */
  private parseKVLine(line: string): Record<string, string> {
    const obj: Record<string, string> = {};
    const parts = line.split(',');
    parts.forEach(part => {
      const [key, value] = this.splitFirstColon(part);
      if (key && value) {
        obj[key.trim()] = value.trim();
      }
    });
    return obj;
  }

  /**
   * Split on the FIRST colon only (values can contain colons, e.g., URLs).
   * Returns [undefined, undefined] for bare "null" entries.
   */
  private splitFirstColon(str: string): [string | undefined, string | undefined] {
    const trimmed = str.trim();
    if (trimmed === 'null' || trimmed === '') return [undefined, undefined];
    const idx = trimmed.indexOf(':');
    if (idx === -1) return [undefined, undefined];
    return [trimmed.substring(0, idx), trimmed.substring(idx + 1)];
  }
}
